/**
 * Content Studio V2 - Form Validation & Error Handling Utilities
 * Shared validation logic, error scrolling, and bilingual error messages
 */

// ============================================================
// BILINGUAL ERROR MESSAGES
// ============================================================

export const ERROR_MESSAGES = {
  required: {
    bm: "Field ini wajib.",
    en: "This field is required."
  },
  selectAtLeastOne: {
    bm: "Pilih sekurang-kurangnya satu.",
    en: "Select at least one."
  },
  invalidAgeRange: {
    bm: "Julat umur mesti salah satu: 13-17, 18-24, 25-34, 35-44, 45-54, 55-64, 65+.",
    en: "Age range must be one of: 13-17, 18-24, 25-34, 35-44, 45-54, 55-64, 65+."
  },
  needProductForSales: {
    bm: "Untuk objektif Sales, pilih sekurang-kurangnya 1 Product atau Service.",
    en: "For Sales objective, select at least 1 Product or Service."
  },
  needBrandingPack: {
    bm: "Pilih sekurang-kurangnya 1 pack penjenamaan (Values, Direction atau Ambassador).",
    en: "Select at least 1 branding pack (Values, Direction or Ambassador)."
  },
  needAtLeast1Interest: {
    bm: "Sila masukkan 1-5 minat.",
    en: "Please add 1-5 interests."
  },
  selectCountry: {
    bm: "Sila pilih sekurang-kurangnya 1 negara.",
    en: "Please select at least 1 country."
  },
  selectLanguage: {
    bm: "Sila pilih sekurang-kurangnya 1 bahasa.",
    en: "Please select at least 1 language."
  },
  selectLocationType: {
    bm: "Sila pilih sekurang-kurangnya 1 jenis lokasi.",
    en: "Please select at least 1 location type."
  },
  emptyAnswer: {
    bm: "Jawapan tidak boleh kosong.",
    en: "Answer cannot be empty."
  },
  sessionExpired: {
    bm: "Sesi tamat tempoh — sila log masuk semula.",
    en: "Session expired — please login again."
  },
  generalError: {
    bm: "Sila semak medan yang ditandakan merah.",
    en: "Please check the highlighted fields."
  }
};

/**
 * Get error message in specified locale
 * @param {string} errorKey - Error message key
 * @param {string} locale - 'bm' or 'en', defaults to 'en'
 * @returns {string} Error message
 */
export const getErrorMessage = (errorKey, locale = 'en') => {
  const message = ERROR_MESSAGES[errorKey];
  if (!message) return ERROR_MESSAGES.generalError[locale];
  return message[locale] || message.en;
};

// ============================================================
// FORM VALIDATION UTILITIES
// ============================================================

/**
 * Validate Step 2 (Audience) form data
 * @param {object} values - Form values
 * @returns {object} errors - { fieldName: errorKey }
 */
export const validateAudienceForm = (values) => {
  const errors = {};

  // Countries (required, min 1)
  if (!values.countries || values.countries.length === 0) {
    errors.countries = 'selectCountry';
  }

  // Age ranges (required, min 1)
  if (!values.age_ranges || values.age_ranges.length === 0) {
    errors.age_ranges = 'selectAtLeastOne';
  }

  // Location types (required, min 1)
  if (!values.location_types || values.location_types.length === 0) {
    errors.location_types = 'selectLocationType';
  }

  // Interests (required, 1-5)
  if (!values.interests || values.interests.length === 0) {
    errors.interests = 'needAtLeast1Interest';
  } else if (values.interests.length > 5) {
    errors.interests = 'needAtLeast1Interest';
  }

  // Languages (required, min 1)
  if (!values.languages || values.languages.length === 0) {
    errors.languages = 'selectLanguage';
  }

  return errors;
};

/**
 * Validate Step 3 (Knowledge Selection) form data
 * @param {object} values - Form values
 * @param {string} objective - Objective type (Sales, Branding, Awareness)
 * @returns {object} errors - { fieldName: errorKey }
 */
export const validateKnowledgeForm = (values, objective) => {
  const errors = {};

  // Sales objective requires at least 1 Product or Service
  if (objective === 'Sales') {
    const productsCount = values.selectedProducts?.length || 0;
    const servicesCount = values.selectedServices?.length || 0;
    
    if (productsCount === 0 && servicesCount === 0) {
      errors.productPacks = 'needProductForSales';
    }
  }
  
  // Branding/Awareness requires at least 1 of: Values, Direction, Ambassador
  if (objective === 'Branding' || objective === 'Awareness') {
    const valuesCount = values.selectedValues?.length || 0;
    const directionCount = values.selectedDirection?.length || 0;
    const ambassadorCount = values.selectedAmbassador?.length || 0;
    
    if (valuesCount === 0 && directionCount === 0 && ambassadorCount === 0) {
      errors.brandingPacks = 'needBrandingPack';
    }
  }

  return errors;
};

/**
 * Validate SOA Drill answer
 * @param {string} answer - User's answer
 * @returns {object} errors - { fieldName: errorKey }
 */
export const validateSOAAnswer = (answer) => {
  const errors = {};

  if (!answer || answer.trim().length === 0) {
    errors.answer = 'emptyAnswer';
  }

  return errors;
};

// ============================================================
// ERROR SCROLLING & FOCUS UTILITIES
// ============================================================

/**
 * Scroll to first error field and focus it
 * @param {object} options
 * @param {string[]} options.errorOrder - Array of field names in priority order
 * @param {object} options.refs - Refs object { fieldName: ref }
 * @param {object} options.errors - Current errors object
 * @param {number} options.headerOffset - Pixels to offset for fixed header (default 92)
 */
export const scrollToFirstError = ({ errorOrder, refs, errors, headerOffset = 92 }) => {
  if (!errors || Object.keys(errors).length === 0) return;

  // Find first field in errorOrder that has an error
  const firstErrorField = errorOrder.find(field => errors[field]);
  
  if (!firstErrorField) return;

  const ref = refs[firstErrorField];
  
  if (ref && ref.current) {
    // Calculate scroll position with header offset
    const element = ref.current;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

    // Smooth scroll to element
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });

    // Focus after scroll animation (300ms delay)
    setTimeout(() => {
      // Try to focus the element
      if (element.focus) {
        element.focus({ preventScroll: true });
      } else if (element.querySelector) {
        // If container, try to focus first input inside
        const firstInput = element.querySelector('input, button, textarea, select, [tabindex="0"]');
        if (firstInput && firstInput.focus) {
          firstInput.focus({ preventScroll: true });
        }
      }
    }, 300);

    // Vibrate on mobile (optional feedback)
    if (navigator.vibrate) {
      navigator.vibrate(20);
    }
  }
};

/**
 * Map backend validation errors to frontend error keys
 * @param {object} backendError - Backend error response
 * @returns {object} errors - { fieldName: errorKey }
 */
export const mapBackendErrors = (backendError) => {
  const errors = {};

  // Handle FastAPI 422 validation errors
  if (backendError.detail && Array.isArray(backendError.detail)) {
    backendError.detail.forEach(err => {
      // Extract field name from location path
      // e.g., ['body', 'audience', 'age_ranges'] -> 'age_ranges'
      const loc = err.loc || [];
      const fieldName = loc[loc.length - 1];
      
      if (fieldName) {
        errors[fieldName] = 'required'; // or parse err.msg for specific error
      }
    });
  }

  return errors;
};

/**
 * Create refs object for form fields
 * @param {string[]} fieldNames - Array of field names
 * @returns {object} refs - { fieldName: React.createRef() }
 */
export const createFieldRefs = (fieldNames) => {
  const refs = {};
  fieldNames.forEach(name => {
    refs[name] = { current: null };
  });
  return refs;
};
